import { gql } from '@/__generated__/gql';
import { DocumentRecordsListSortBy, DocumentRecordsSortDirection, StrategyDetailsPageListDocumentRecordsQuery } from '@/__generated__/graphql';
import { AddButtonIcon } from '@/assets/static/icons/add-button-icon';
import Button from '@/components/basicComponents/button';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { initialValuesFromTable } from '@/components/table/constants';
import FilterPage from '@/components/table/filterPage';
import TablePagination from '@/components/table/tablePagination';
import { IItems } from '@/components/table/types';
import { IMobileSort } from '@/components/transactions-page/types';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useGoBack } from '@/hooks/useGoBack';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { NetworkStatus, useQuery } from '@apollo/client';
import { DocumentRecordStatus, TenantType } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { NavigateOptions, useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { allManagerUpdatesTableMobileSort, MANAGER_UPDATES_EDITABLE_ROLES } from '../../../../managerUpdates-page/constants';
import AddUpdate from '../modals/addUpdate';
import ChangeOwner from '../modals/changeOwner';
import DeleteUpdate from '../modals/deleteUpdate';
import DownloadPDF from '../modals/downloadPDF';
import PublishUpdate from '../modals/publishUpdate';
import UnpublishUpdate from '../modals/unpublishUpdate';
import { STRATEGY_DETAILS_PAGE_LIST_DOCUMENT_RECORDS_QUERY } from '../queries';
import MobileTable from './table/mobileTable';
import { Table } from './table/table';

const updatesFilterOptions = Object.entries(DocumentRecordStatus).map(([key, value]) => ({
  label: value,
  value: key
}));

const GET_INVESTMENT_MANAGER_ID_QUERY = gql(`
  query AllManagerUpdatesPageInvestment($id: String!) {
    Investment(id: $id) {
      id
      name
      managerId
    }
  }
`);

export const AllManagerUpdates = () => {
  const navigate = useNavigate();
  const goBack = useGoBack();
  const location = useLocation();
  const params = useParams();
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { searchParams, setSearchParams, onChangeSearch, onChangeSort, onChangePaginationData } = useSearchParamsManager();

  const backToTitle = searchParams.get('backToTitle') || '';
  const limit = searchParams.get('limit') || initialValuesFromTable.initialAmountRowsPerPage[0];
  const offset = searchParams.get('offset') || 0;
  const q = searchParams.get('q') || '';
  const key = searchParams.get('key') || DocumentRecordsListSortBy.Name;
  const asc = searchParams.get('asc') === null || searchParams.get('asc') === 'true';
  const statusFilter = searchParams.get('statusFilter') || '';
  const position = searchParams.get('position') || 0;

  const [allManagerUpdatesTableData, setAllManagerUpdatesTableData] = useState<
    StrategyDetailsPageListDocumentRecordsQuery['listDocumentRecords']['records']
  >([]);
  const [currentRow, setCurrentRow] = useState<StrategyDetailsPageListDocumentRecordsQuery['listDocumentRecords']['records'][number] | null>(null);
  const [managerId, setManagerId] = useState('');
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [publishDate, setPublishDate] = useState('');
  const [mobileSortItems, setMobileSortItems] = useState<IMobileSort[]>([]);
  const [mobileSort, setMobileSort] = useState(allManagerUpdatesTableMobileSort[0].title);
  const sortItem = mobileSortItems.find((item) => item.title === mobileSort);
  const mobileSortList = mobileSortItems.map((item) => item.title);

  useEffect(() => {
    if (MANAGER_UPDATES_EDITABLE_ROLES.some((role) => user.roles.includes(role)) && user.tenant.type === TenantType.PLATFORM_MANAGER) {
      setMobileSortItems(allManagerUpdatesTableMobileSort);
      return;
    }

    goBack({ fallBack: '/dealCatalog' });
    const filteredMobileSortItems = allManagerUpdatesTableMobileSort.filter((item) => item.key !== 'OWNER');
    setMobileSortItems(filteredMobileSortItems);
  }, [user]);

  const { loading: investmentLoading } = useQuery<{ Investment: { id: string; name: string; managerId: string } }>(GET_INVESTMENT_MANAGER_ID_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip: !params?.strategyId,
    variables: {
      id: params?.strategyId
    },
    onCompleted: (data) => {
      setManagerId(data?.Investment?.managerId ?? '');
    }
  });

  const {
    data: listDocumentRecordsData,
    networkStatus: listDocumentRecordsNetworkStatus,
    refetch: listDocumentRecordsRefetch
  } = useQuery(STRATEGY_DETAILS_PAGE_LIST_DOCUMENT_RECORDS_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !params?.strategyId,
    variables: {
      data: {
        investmentId: params?.strategyId,
        search: q,
        sortBy: key as DocumentRecordsListSortBy,
        sortDirection: asc ? DocumentRecordsSortDirection.Asc : DocumentRecordsSortDirection.Desc,
        ...(statusFilter && { statusFilter: [statusFilter] }),
        documentTypeFilter: ['mu'],
        limit: Number(limit),
        offset: Number(offset)
      },
      fieldNames: ['Document Record Name', 'Document Record Publish Date']
    },
    onCompleted: (data) => {
      setAllManagerUpdatesTableData(data.listDocumentRecords.records);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  useEffect(() => {
    if (!isMobile) return;
    listDocumentRecordsRefetch({
      data: {
        investmentId: params?.strategyId,
        search: q,
        sortDirection: sortItem?.asc ? DocumentRecordsSortDirection.Asc : DocumentRecordsSortDirection.Desc,
        sortBy: sortItem?.key as DocumentRecordsListSortBy,
        ...(statusFilter && { statusFilter: [statusFilter] }),
        documentTypeFilter: ['mu'],
        limit: Number(limit),
        offset: Number(offset)
      },
      fieldNames: ['Document Record Name', 'Document Record Publish Date']
    });
  }, [mobileSort]);

  const onChangeStatusFilter = (filter: string) => {
    setSearchParams(
      (prev) => {
        !!filter ? prev.set('statusFilter', filter) : prev.delete('statusFilter');
        prev.delete('limit');
        prev.delete('offset');
        return prev;
      },
      { replace: true }
    );
  };

  const openModalWindow = (type: string, row?: StrategyDetailsPageListDocumentRecordsQuery['listDocumentRecords']['records'][number]) => {
    setModalWindow({ ...modalWindow, isOpen: true, type });
    if (row) {
      setCurrentRow(row);
    }
    if (type === 'publish-update') {
      const publishDateField = row?.documentRecordFieldAnswers.find((field) => field.documentField.name === 'Document Record Publish Date');
      const publishDate = publishDateField?.AI?.answers?.[0] ?? publishDateField?.value ?? '';
      setPublishDate(publishDate);
    }
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
    setCurrentRow(null);
    setPublishDate('');
  };

  const openPage = (path: string, options?: NavigateOptions) => {
    navigate(path, options);
  };

  const openViewUpdate = (row: StrategyDetailsPageListDocumentRecordsQuery['listDocumentRecords']['records'][number]) => {
    const opportunityDetailsPagePath = location?.pathname?.replace(/\/allUpdates.*$/, '');
    openPage(`${opportunityDetailsPagePath}?tab=Updates&documentRecordId=${row.id}&backToTitle=All Updates`);
  };

  const dropdownActions = (row: StrategyDetailsPageListDocumentRecordsQuery['listDocumentRecords']['records'][number]): IItems[] => {
    return [
      {
        name: 'Edit Update',
        onClick: () => openPage(`edit/${row.id}?backToTitle=${backToTitle}`)
      },
      {
        name: 'View Update',
        onClick: () => openViewUpdate(row)
      },
      {
        name: 'Download',
        onClick: () => openModalWindow('download-pdf', row)
      },
      {
        name: 'Change Owner',
        onClick: () => openModalWindow('change-owner', row)
      },
      ...(row?.status === 'Draft'
        ? [
            {
              name: 'Publish Update',
              onClick: () => openModalWindow('publish-update', row)
            }
          ]
        : []),
      ...(row?.status === 'Published'
        ? [
            {
              name: 'Unpublish Update',
              onClick: () => openModalWindow('unpublish-update', row)
            }
          ]
        : []),
      {
        name: 'Delete',
        onClick: () => openModalWindow('delete-update', row)
      }
    ];
  };

  if (investmentLoading) return <LoaderOnWholeScreen />;

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'add-update' && (
        <AddUpdate
          onClose={closeModalWindow}
          isOpen={modalWindow.isOpen}
          manager={managerId}
          openEditManagerUpdatePage={(updateId, options) => openPage(`edit/${updateId}?backToTitle=${backToTitle}`, options)}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'publish-update' && (
        <PublishUpdate
          isOpen={modalWindow.isOpen}
          publishDate={publishDate}
          updateId={currentRow?.id ?? null}
          ownerEmail={currentRow?.owner?.email ?? null}
          refetch={listDocumentRecordsRefetch}
          onClose={closeModalWindow}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'unpublish-update' && (
        <UnpublishUpdate
          isOpen={modalWindow.isOpen}
          onClose={closeModalWindow}
          updateId={currentRow?.id ?? null}
          refetch={listDocumentRecordsRefetch}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'delete-update' && (
        <DeleteUpdate
          isOpen={modalWindow.isOpen}
          onClose={closeModalWindow}
          currentRow={currentRow}
          listDocumentRecordsRefetch={listDocumentRecordsRefetch}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'change-owner' && (
        <ChangeOwner
          isOpen={modalWindow.isOpen}
          onClose={closeModalWindow}
          currentRow={currentRow}
          listDocumentRecordsRefetch={listDocumentRecordsRefetch}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'download-pdf' && (
        <DownloadPDF isOpen={modalWindow.isOpen} onClose={closeModalWindow} updateId={currentRow?.id ?? null} source="updateTab" />
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={() => goBack({ fallBack: '/managerUpdates' })} backToTitle={backToTitle} />} />
        <PageTitle title="All Updates" />
      </MainWrap>
      <MainWrap>
        <PaddingWrap>
          <AddNewUpdateButton size="md" onClick={() => openModalWindow('add-update')} icon={<AddButtonIcon />}>
            Add New Update
          </AddNewUpdateButton>
          <FilterPage
            search={{
              value: q,
              onChange: onChangeSearch,
              placeholder: 'Search'
            }}
            quickFilter={{
              title: 'Updates',
              defaultValue: 'All',
              value: statusFilter,
              onChange: onChangeStatusFilter,
              options: [{ label: 'All', value: '' }, ...updatesFilterOptions]
            }}
            mobileSort={{
              value: mobileSort,
              onChange: setMobileSort,
              selects: mobileSortList
            }}
            resultsValue={listDocumentRecordsData?.listDocumentRecords?.total ?? 0}
            refetch={listDocumentRecordsRefetch}
            useNewFilters
          />
          {isMobile ? (
            <MobileTable
              tableData={allManagerUpdatesTableData}
              dropdownActions={dropdownActions}
              openEditManagerUpdatePage={(updateId) => openPage(`edit/${updateId}?backToTitle=All Updates`)}
            />
          ) : (
            <Table
              tableData={allManagerUpdatesTableData}
              sort={{ asc, key }}
              loading={listDocumentRecordsNetworkStatus === NetworkStatus.loading || listDocumentRecordsNetworkStatus === NetworkStatus.setVariables}
              setSort={onChangeSort}
              dropdownActions={dropdownActions}
              openEditManagerUpdatePage={(updateId) => openPage(`edit/${updateId}?backToTitle=All Updates`)}
            />
          )}
          <PaginationWrap>
            <TablePagination
              savePagination={onChangePaginationData}
              paginationValues={{
                limit: Number(limit),
                offset: Number(offset),
                total: listDocumentRecordsData?.listDocumentRecords?.total ?? 0
              }}
              refetch={() => null as any}
            />
          </PaginationWrap>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const AddNewUpdateButton = styled(Button)`
  margin: 50px 0 10px;
`;

const PaginationWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;
